import {
  UseInfiniteQueryOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from "@tanstack/react-query";

import { PaginatedResult } from "@/interfaces/common/PaginatedResult";
import { OrderItem } from "@/interfaces/domain/Order";
import { StoreHall } from "@/interfaces/domain/StoreHall";
import {
  StoreHallWithProducts,
  getStoreHallByIdRequest,
  getStoreHallProductListRequest,
  getStoreHallsRequest,
} from "@/services/api/storeHall";
import { logger } from "@/services/logger";
import { formatProductToOrderItem } from "@/utils/helpers";

export type StoreHallWithOrderItems = StoreHall & { produtos: OrderItem[] };

export const useStoreHalls = (storeId: string, options?: UseQueryOptions<StoreHall[]>) => {
  return useQuery<StoreHall[]>({
    queryKey: [`/store-halls/${storeId}`],
    queryFn: () => getStoreHallsRequest(storeId),
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
};

export const useStoreHallWithProducts = (
  storeId: string,
  options?: UseInfiniteQueryOptions<Partial<PaginatedResult<StoreHallWithProducts>>>
) => {
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery<
    Partial<PaginatedResult<StoreHallWithProducts>>
  >({
    queryKey: [`/store-halls-with-products/${storeId}`],
    queryFn: async ({ pageParam = 1 }) => {
      const data = await getStoreHallProductListRequest({
        page: pageParam || 1,
        storeId,
      });
      return {
        docs: data,
        page: pageParam || 1,
      };
    },
    getNextPageParam: (lastPage) => lastPage.page + 1,
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
  return {
    data: data?.pages
      .map((page) => page.docs)
      .flat()
      .map((hall) => ({
        ...hall,
        produtos: hall?.produtos?.map(formatProductToOrderItem),
      })) as StoreHallWithOrderItems[],
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  };
};

export const useHallById = (id: string, options?: UseQueryOptions<StoreHall>) => {
  return useQuery<StoreHall>({
    queryKey: [`/store-hall/${id}`],
    queryFn: () => getStoreHallByIdRequest(id),
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });
};
